import { api } from '@/lib/api';

const KEY = 'yr_voice_settings';
export const VOICE_OPTIONS = [
  { id: 'nova', label: 'Nova', desc: 'Warm, energetic · female' },
  { id: 'shimmer', label: 'Shimmer', desc: 'Bright, upbeat · female' },
  { id: 'coral', label: 'Coral', desc: 'Friendly, clear · female' },
  { id: 'onyx', label: 'Onyx', desc: 'Calm, deep · male' },
  { id: 'echo', label: 'Echo', desc: 'Smooth, steady · male' },
  { id: 'ash', label: 'Ash', desc: 'Clear, direct · male' },
];
export const DEFAULT_VOICE_SETTINGS = { enabled: true, voice: 'nova', volume: 0.9, rate: 0.8, motivation: true, restAnnouncements: true, strengthMode: 'continuous' };
export const loadVoiceSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || '{}');
    // Speed 1.0 saved before speed was tracked = the old default, not a choice → new 0.80× default.
    if (!saved.rateSet) delete saved.rate;
    return { ...DEFAULT_VOICE_SETTINGS, ...saved };
  } catch { return { ...DEFAULT_VOICE_SETTINGS }; }
};
export const saveVoiceSettings = (s) => localStorage.setItem(KEY, JSON.stringify(s));

const SILENT = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRBqpAAAAAAD/+xDEAAPAAAGkAAAAIAAANIAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sQxCmDwAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';

/** Plays pre-generated natural speech clips on demand. It never decides timing — the workout timer calls say(). */
export class VoiceCoach {
  constructor(settings) {
    this.s = settings;
    this.clips = new Map();
    this.pending = new Map();
    this.queue = [];
    this.synthBusy = false;
    this.audio = new Audio();
    this.audio.preservesPitch = true;
    this.audio.onended = () => this._next();
  }

  update(s) { this.s = s; this.audio.volume = s.volume; this.audio.playbackRate = s.rate; if (!s.enabled) this.stop(); }
  _key(t) { return `${this.s.voice}|${t}`; }

  fetch(text) {
    const k = this._key(text);
    if (this.clips.has(k)) return Promise.resolve();
    if (this.pending.has(k)) return this.pending.get(k);
    const p = api.post('/voice/tts', { text, voice: this.s.voice }, { responseType: 'blob' })
      .then(r => { this.clips.set(k, URL.createObjectURL(r.data)); })
      .catch(() => {})
      .finally(() => this.pending.delete(k));
    this.pending.set(k, p);
    return p;
  }

  async prefetch(texts, onProgress, isAlive = () => true) {
    const list = [...new Set(texts.filter(Boolean))];
    let done = 0, i = 0;
    const worker = async () => {
      while (i < list.length && isAlive()) { const t = list[i++]; await this.fetch(t); done += 1; onProgress?.(done, list.length); }
    };
    await Promise.all(Array.from({ length: 6 }, worker));
  }

  get busy() { return (!this.audio.paused && !this.audio.ended) || this.synthBusy; }

  say(text, mode = 'interrupt') {
    if (!this.s.enabled || !text) return;
    if (mode === 'ifIdle' && this.busy) return;
    if (mode === 'queue' && this.busy) { this.queue.push(text); return; }
    this.queue = [];
    this._play(text);
  }

  _play(text) {
    const url = this.clips.get(this._key(text));
    if (window.speechSynthesis && this.synthBusy) { window.speechSynthesis.cancel(); this.synthBusy = false; }
    if (url) {
      this.audio.src = url;
      this.audio.volume = this.s.volume;
      this.audio.defaultPlaybackRate = this.s.rate;
      this.audio.playbackRate = this.s.rate;
      this.audio.play().catch(() => {});
      return;
    }
    this.fetch(text);
    this._synth(text);
  }

  // Fallback only when a clip isn't ready yet, so a cue is never silently dropped.
  _synth(text) {
    if (!window.speechSynthesis) return;
    this.audio.pause();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = this.s.rate; u.volume = this.s.volume;
    u.onend = () => { this.synthBusy = false; this._next(); };
    this.synthBusy = true;
    window.speechSynthesis.speak(u);
  }

  _next() { const t = this.queue.shift(); if (t) this._play(t); }
  unlock() { this.audio.src = SILENT; this.audio.play().catch(() => {}); }
  pause() { this.audio.pause(); window.speechSynthesis?.pause(); }
  resume() { if (this.audio.src && !this.audio.ended && this.audio.currentTime > 0) this.audio.play().catch(() => {}); window.speechSynthesis?.resume(); }
  stop() { this.queue = []; this.audio.pause(); if (window.speechSynthesis) window.speechSynthesis.cancel(); this.synthBusy = false; }
  dispose() { this.stop(); this.clips.forEach(u => URL.revokeObjectURL(u)); this.clips.clear(); }
}
